import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import OrdersInfoListEmpty from '@/features/orders/components/OrdersInfo/OrdersInfoListEmpty';
import OrderDetails from '@/features/orders/components/OrderDetails';
import { useOrdersListApi } from '@/features/orders/hooks/useOrdersListApi';
import { ordersUtils } from '@/features/orders/utils/ordersUtils';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { OrderListItemResponseDto } from '@/features/orders/types/orders-dto';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

type Props = {
	isMobile?: boolean;
};

const OrdersInfoList: FC<Props> = ({ isMobile }) => {
	const [showDetails, setShowDetails] = useState(false);
	const [orderId, setOrderId] = useState('');

	const navigate = useNavigate();
	const { t } = useTranslation();

	const { ordersListData, isLoadingOrdersListData } = useOrdersListApi();

	const showOrderHandler = (id: string) => {
		setOrderId(id);
		setShowDetails(true);
	};

	const groupedActivities =
		ordersListData?.items?.reduce((acc, curr) => {
			const dateKey = format(new Date(curr.documentDate), 'yyyy-MM-dd');

			if (!acc[dateKey]) {
				acc[dateKey] = [];
			}
			acc[dateKey].push(curr);

			return acc;
		}, {} as Record<string, OrderListItemResponseDto[]>) || [];

	return (
		<div className='grow flex flex-col gap-4'>
			<div className='flex-1'>
				{isLoadingOrdersListData && (
					<EActivitiesListSkeleton isMobile={isMobile} />
				)}
				{!isLoadingOrdersListData &&
					!!ordersListData?.items?.length && (
						<div
							className={cn(
								'flex flex-col gap-6',
								isMobile && 'gap-4',
							)}>
							{Object.entries(groupedActivities).map(
								([title, events]) => {
									return (
										<div key={title}>
											<h5 className='text-sm text-popover-800'>
												{DateTimeHelper.formatDate(
													new Date(title),
													2,
												)}
											</h5>
											{events.map((event) => {
												const {
													label,
													icon,
													colorClass,
												} = ordersUtils.getStatusData(
													event.status,
												);

												return (
													<EActivitiesElement
														key={event.id}
														icon={icon}
														colorClass={colorClass}
														header={
															event.services ??
															t(
																'main.orders.components.list.noServices',
															)
														}
														description={label}
														value={
															event.totalAmount
														}
														id={event.id}
														onClick={
															showOrderHandler
														}
														isMobile={isMobile}
													/>
												);
											})}
										</div>
									);
								},
							)}
						</div>
					)}
				{!isLoadingOrdersListData && !ordersListData?.items?.length && (
					<OrdersInfoListEmpty />
				)}
			</div>
			<Button
				variant={'outline'}
				className='w-full text-sm font-medium'
				onClick={() => navigate('/orders')}>
				{t('main.orders.components.list.seeMoreButton')}
			</Button>
			{showDetails && (
				<OrderDetails
					id={orderId}
					onClose={() => setShowDetails(false)}
					isMobile={isMobile}
				/>
			)}
		</div>
	);
};

export default OrdersInfoList;
